import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

export function ImagePickerDialog({ open, onClose, pageId, slot, onChanged }) {
  const [tab, setTab] = useState("library");
  const [suggestions, setSuggestions] = useState([]);
  const [generic, setGeneric] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !slot) return;
    setTab("library");
    setLoading(true);
    axios.get(`${API}/pages/${pageId}/image-suggestions`, { params: { slot }, headers: authHeaders() })
      .then(r => { setSuggestions(r.data.suggestions || []); setGeneric(r.data.generic || {}); })
      .catch(() => toast.error("Bilder konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [open, slot, pageId]);

  const apply = async (url) => {
    setSaving(true);
    try {
      await axios.put(`${API}/pages/${pageId}/images`, { slot, url }, { headers: authHeaders() });
      toast.success("Bild ersetzt");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bild konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/pages/${pageId}/upload-image`, fd, { headers: authHeaders() });
      await apply(res.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
      setSaving(false);
    }
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="image-picker-dialog">
        <DialogHeader>
          <DialogTitle>Anderes Bild wählen</DialogTitle>
          <DialogDescription>Wähle ein passendes Bild aus der Bibliothek oder lade ein eigenes hoch.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[["library", "Passend zur Branche"], ["generic", "Allgemeine Motive"], ["upload", "Eigenes Bild hochladen"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`image-tab-${id}`}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab !== "upload" ? (
          loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-4" data-testid="image-library-grid">
              {(tab === "library" ? [["", suggestions]] : Object.entries(generic)).map(([cat, urls]) => (
                <div key={cat || "all"}>
                  {cat && <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{cat}</p>}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {urls.map((url, i) => (
                      <button key={url} disabled={saving} onClick={() => apply(url)} data-testid={`image-option-${cat || tab}-${i}`}
                        className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 hover:border-indigo-500 hover:shadow-md transition-all">
                        <img src={url.replace(/w=\d+/, "w=400")} alt="Bildvorschlag" className="w-full h-full object-cover" loading="lazy" />
                        <span className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/20 flex items-center justify-center transition-colors">
                          <Check className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {tab === "library" && suggestions.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">Keine weiteren Vorschläge. Probiere „Allgemeine Motive“ oder lade ein eigenes Bild hoch.</p>}
            </div>
          )
        ) : (
          <div className="py-4">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="image-upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={saving} data-testid="image-upload-btn"
              className="w-full border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-2xl py-12 flex flex-col items-center gap-3 text-slate-600 transition-colors">
              {saving ? <Loader2 className="w-7 h-7 animate-spin text-indigo-600" /> : <Upload className="w-7 h-7 text-indigo-600" />}
              <span className="font-medium">JPG, PNG oder WebP wählen (max. 5 MB)</span>
            </button>
            <p className="text-xs text-slate-500 mt-3">Bitte lade nur Bilder hoch, an denen du die Nutzungsrechte besitzt. Abgebildete Personen müssen der Veröffentlichung zugestimmt haben.</p>
          </div>
        )}
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose} data-testid="image-picker-cancel">Abbrechen</Button></div>
      </DialogContent>
    </Dialog>
  );
}
